import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { LifeBuoy, Waves, Mountain, Users, Radio, Mail } from "lucide-react";
import BrandMark from "@/components/BrandMark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSTRUCTOR_IMG =
  "https://images.unsplash.com/photo-1635150632618-b09762cf0690?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxjb2FzdCUyMGd1YXJkJTIwcmVzY3VlJTIwdGVhbXxlbnwwfHx8fDE3OTM4NjUzNzh8MA&ixlib=rb-4.1.0&q=85";


const Nav = () => (
  <header className="sticky top-0 z-40 bg-[#fffbf2]/95 backdrop-blur-md border-b border-[#0f2027]/10 px-6 py-4">
    <div className="max-w-6xl mx-auto flex items-center justify-between">
      <nav className="flex items-center gap-3" data-testid="nav-brand">
        <BrandMark />
        <div className="leading-none">
          <span className="block font-display text-sm tracking-wider uppercase text-[#0f2027]">PROTECCIÓN CIVIL VIGO</span>
          <span className="block text-[10px] tracking-widest text-[#e65100] font-mono-tactical uppercase">
            Teleformación & Rescate
          </span>
        </div>
      </nav>
      <div className="hidden md:flex items-center gap-8 text-xs font-mono-tactical uppercase tracking-widest text-[#0f2027]/80">
        <a href="#cursos" data-testid="nav-cursos-link" className="hover:text-[#e65100] transition-colors">
          Catálogo
        </a>
        <a href="#metodo" data-testid="nav-instructores-link" className="hover:text-[#e65100] transition-colors">
          Instructores
        </a>
        <a href="#asociacion" data-testid="nav-asociacion-link" className="hover:text-[#e65100] transition-colors">
          Asociación
        </a>
        <a
          href="/gestion"
          data-testid="nav-gestion-link"
          className="hover:text-[#e65100] transition-colors"
        >
          Gestión
        </a>
      </div>
      <div className="flex items-center gap-3">
        <Link
          to="/login"
          data-testid="nav-login-btn"
          className="text-[#0f2027] px-4 py-2 text-xs font-mono-tactical uppercase tracking-wider font-semibold rounded border border-[#0f2027]/30 hover:border-[#e65100] hover:text-[#e65100] transition-colors"
        >
          Acceder
        </Link>
        <a
          href="#contacto"
          data-testid="nav-inscribirse-btn"
          className="bg-[#e65100] text-white px-4 py-2 text-xs font-mono-tactical uppercase tracking-wider font-semibold rounded hover:bg-[#c2410c] transition-colors"
        >
          Inscribirse
        </a>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="py-20 md:py-28 px-6 bg-[#fffbf2] text-[#0f2027] border-b border-[#0f2027]/10 overflow-hidden relative">
    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-8 space-y-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#e65100]/10 text-[#e65100] text-xs font-mono-tactical uppercase tracking-widest fade-in-1">
          <span className="w-2 h-2 rounded-full bg-[#e65100] animate-ping"></span>
          Campus Online Abierto 24/7
        </div>

        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[76px] leading-[0.95] uppercase tracking-tight text-[#0f2027]">
          <span className="ln">
            <span>Formación real</span>
          </span>
          <span className="ln">
            <span className="text-[#e65100]">para salvar vidas</span>
          </span>
        </h1>

        <p className="text-lg md:text-xl text-[#0f2027]/80 max-w-xl leading-relaxed fade-in-2">
          Teleformación técnica y rescate práctico impartido por instructores operativos en Galicia.
        </p>

        <div className="pt-4 flex flex-wrap items-center gap-4 fade-in-3">
          <Link
            to="/login"
            data-testid="hero-cursos-btn"
            className="bg-[#0f2027] text-white px-7 py-3.5 text-xs font-mono-tactical uppercase tracking-widest font-semibold rounded hover:bg-[#e65100] transition-colors shadow-lg"
          >
            Acceder a los cursos
          </Link>
          <a
            href="#bienvenida"
            data-testid="hero-conocenos-btn"
            className="border border-[#0f2027]/30 text-[#0f2027] px-6 py-3.5 text-xs font-mono-tactical uppercase tracking-widest rounded hover:border-[#e65100] hover:text-[#e65100] transition-colors"
          >
            Conócenos
          </a>
        </div>
      </div>

      <div className="lg:col-span-4 flex justify-center">
        <div className="relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center">
          <svg className="w-full h-full pulse-radar" viewBox="0 0 200 200" fill="none" aria-hidden="true">
            <circle cx="100" cy="100" r="90" stroke="#0f2027" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.3" />
            <circle cx="100" cy="100" r="65" stroke="#e65100" strokeWidth="2" opacity="0.4" />
            <circle cx="100" cy="100" r="40" stroke="#0f2027" strokeWidth="1.5" opacity="0.5" />
            <line x1="100" y1="10" x2="100" y2="190" stroke="#0f2027" strokeWidth="1" opacity="0.2" />
            <line x1="10" y1="100" x2="190" y2="100" stroke="#0f2027" strokeWidth="1" opacity="0.2" />
            <path d="M100 100 L170 60 A 80 80 0 0 0 100 20 Z" fill="#e65100" fillOpacity="0.15" />
            <circle cx="140" cy="55" r="4" fill="#e65100" />
            <circle cx="65" cy="130" r="3" fill="#0f2027" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 pointer-events-none">
            <span className="text-xs font-mono-tactical text-[#e65100] uppercase tracking-widest font-bold">
              AByS Galicia
            </span>
            <span className="font-display text-2xl uppercase text-[#0f2027]">Campus 24/7</span>
            <span className="text-[11px] text-[#0f2027]/70 font-mono-tactical">100% Flexibilidad</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Marquee = () => (
  <div className="bg-[#e65100] text-white py-3 overflow-hidden border-y border-[#0f2027]/20">
    <div className="mq text-xs font-mono-tactical uppercase tracking-widest font-bold gap-8 items-center">
      {[0, 1].map((i) => (
        <span key={i} className="flex gap-8 shrink-0">
          <span>• Primeros Auxilios Básicos en Montaña</span>
          <span>• Formación en Coeducación</span>
          <span>• Salvamento Marítimo</span>
          <span>• Búsqueda de Personas</span>
          <span>• Protección Civil Vigo</span>
          <span>• Instructores Operativos en Activo</span>
        </span>
      ))}
    </div>
  </div>
);

const Bienvenida = () => (
  <section id="bienvenida" className="py-24 px-6 bg-[#f7eedc] text-[#0f2027] border-b border-[#0f2027]/10">
    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-7 space-y-6">
        <span className="text-xs font-mono-tactical uppercase tracking-widest text-[#e65100]">Bienvenida</span>
        <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight text-[#0f2027]">
          ¿Qué es Protección Civil Vigo?
        </h2>
        <p className="text-base md:text-lg text-[#0f2027]/80 leading-relaxed" data-testid="bienvenida-text">
          Bienvenid@ a la plataforma de teleformación de la asociación de Búsqueda y Salvamento de Galicia. Siempre
          disponible, para adaptarse a vuestros horarios y necesidades.
        </p>
        <p className="text-sm md:text-base text-[#0f2027]/80 leading-relaxed">
          Protección Civil en Vigo es el sistema de apoyo voluntario que respalda a los servicios de emergencia ante
          catástrofes, accidentes y situaciones de riesgo en la ciudad y su entorno. Desde esta asociación formamos a
          las personas que intervienen cuando más se necesita: en la mar, en la montaña y en la búsqueda de personas
          desaparecidas.
        </p>
        <p className="text-sm md:text-base text-[#0f2027]/80 leading-relaxed">
          Disponemos de un catálogo de formación accesible para que puedas seleccionar aquellas acciones formativas que
          resulten de interés para tu desarrollo personal y profesional.
        </p>
      </div>
      <div className="lg:col-span-5 space-y-4">
        <div className="p-6 bg-white border-2 border-[#0f2027] rounded-lg shadow-[6px_6px_0px_#0f2027] flex items-start gap-4">
          <Waves className="w-8 h-8 text-[#e65100] shrink-0" aria-hidden="true" />
          <div>
            <h3 className="font-display text-xl uppercase text-[#0f2027]">Salvamento Marítimo</h3>
            <p className="text-xs text-[#0f2027]/70 mt-1 leading-relaxed">
              Formación en rescate acuático y actuación en la costa gallega.
            </p>
          </div>
        </div>
        <div className="p-6 bg-white border-2 border-[#0f2027] rounded-lg shadow-[6px_6px_0px_#e65100] flex items-start gap-4">
          <Mountain className="w-8 h-8 text-[#e65100] shrink-0" aria-hidden="true" />
          <div>
            <h3 className="font-display text-xl uppercase text-[#0f2027]">Búsqueda de Personas</h3>
            <p className="text-xs text-[#0f2027]/70 mt-1 leading-relaxed">
              Técnicas de rastreo y localización de personas desaparecidas en terreno abierto.
            </p>
          </div>
        </div>
        <div className="p-6 bg-white border-2 border-[#0f2027] rounded-lg shadow-[6px_6px_0px_#0f2027] flex items-start gap-4">
          <Users className="w-8 h-8 text-[#e65100] shrink-0" aria-hidden="true" />
          <div>
            <h3 className="font-display text-xl uppercase text-[#0f2027]">Voluntariado Operativo</h3>
            <p className="text-xs text-[#0f2027]/70 mt-1 leading-relaxed">
              Acompañamiento para incorporarte a un equipo de intervención en emergencias.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const COURSES = [
  {
    tag: "Activo",
    tagColor: "bg-[#e65100]",
    lessons: "50 Lecciones Online",
    title: "Primeros Auxilios Básicos - Montaña",
    desc: "Protocolos PAS, inmovilizaciones de emergencia, hipotermia y triage en terreno hostil y montaña gallega.",
    shadow: "shadow-[6px_6px_0px_#0f2027]",
  },
  {
    tag: "Activo",
    tagColor: "bg-[#0f2027]",
    lessons: "44 Lecciones Online",
    title: "Formación en Coeducación",
    desc: "Módulos pedagógicos para formadores de salvamento, trabajo en equipo y convivencia en operativos.",
    shadow: "shadow-[6px_6px_0px_#e65100]",
  },
  {
    tag: "Salvamento Marítimo",
    tagColor: "bg-[#e65100]",
    lessons: "Próxima edición",
    title: "Salvamento Marítimo",
    desc: "Actuación en rescate acuático: aproximación a la víctima, manejo de material de flotación y señalización en la costa.",
    shadow: "shadow-[6px_6px_0px_#0f2027]",
  },
  {
    tag: "Búsqueda",
    tagColor: "bg-[#0f2027]",
    lessons: "Próxima edición",
    title: "Búsqueda de Personas",
    desc: "Metodología de búsqueda de desaparecidos: batidas, orientación y coordinación de equipos en terreno abierto.",
    shadow: "shadow-[6px_6px_0px_#e65100]",
  },
];

const Cursos = () => (
  <section id="cursos" className="py-24 px-6 bg-[#fffbf2] text-[#0f2027]">
    <div className="max-w-6xl mx-auto space-y-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-[#0f2027]/15 pb-8">
        <div>
          <span className="text-xs font-mono-tactical uppercase tracking-widest text-[#e65100]">
            Teleformación Oficial
          </span>
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight text-[#0f2027] mt-1">
            Cursos Disponibles
          </h2>
        </div>
        <p className="text-sm text-[#0f2027]/70 max-w-md">
          Plataforma siempre disponible para adaptarse a tus horarios, guardias y ritmo de aprendizaje.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {COURSES.map((c) => (
          <article
            key={c.title}
            data-testid={`course-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`p-8 bg-white border-2 border-[#0f2027] rounded-lg ${c.shadow} space-y-6 flex flex-col justify-between`}
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs font-mono-tactical uppercase">
                <span className={`${c.tagColor} text-white px-2.5 py-1 rounded font-bold`}>{c.tag}</span>
                <span className="text-[#0f2027]/70 font-semibold">{c.lessons}</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl uppercase text-[#0f2027]">{c.title}</h3>
              <p className="text-sm text-[#0f2027]/80 leading-relaxed">{c.desc}</p>
            </div>
            <div className="pt-4 border-t border-[#0f2027]/10 flex items-center justify-between">
              <span className="text-xs font-mono-tactical text-[#0f2027]/70">Campus AByS</span>
              <a
                href="#contacto"
                data-testid={`course-cta-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-xs font-mono-tactical uppercase font-bold text-[#e65100] hover:underline"
              >
                Solicitar plaza →
              </a>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const INSTRUCTORS = [
  {
    role: "Instructor Rescate",
    name: "Javier Ferreira",
    desc: "Especialista en emergencias y coordinación operativa de salvamento.",
  },
  {
    role: "Instructor Montaña",
    name: "Samuel Irigoyen Crujeiras",
    desc: "Formador en primeros auxilios avanzados y búsqueda en terreno escarpado.",
  },
  {
    role: "Instructor Técnico",
    name: "David Hernáez Rodríguez",
    desc: "Especialista en metodologías de teleformación y rescate táctico.",
  },
];

const Instructores = () => (
  <section id="metodo" className="py-24 px-6 bg-[#0f2027] text-[#fffbf2]">
    <div className="max-w-6xl mx-auto space-y-12">
      <div className="max-w-xl">
        <span className="text-xs font-mono-tactical uppercase tracking-widest text-[#e65100]">Cuerpo Docente</span>
        <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight text-white mt-1">
          Instructores Reales
        </h2>
        <p className="text-sm text-[#fffbf2]/70 mt-3">
          Tutorización directa por profesionales con experiencia sobre el terreno en Galicia.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {INSTRUCTORS.map((t) => (
          <div
            key={t.name}
            data-testid={`instructor-card-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="p-6 bg-[#162c35] border border-white/10 rounded space-y-3"
          >
            <div className="text-[#e65100] font-mono-tactical text-xs uppercase font-bold">{t.role}</div>
            <h4 className="font-display text-xl uppercase text-white">{t.name}</h4>
            <p className="text-xs text-[#fffbf2]/70 leading-relaxed">{t.desc}</p>
          </div>
        ))}
      </div>

      <div className="p-6 border border-white/10 rounded bg-[#162c35]/40 text-center space-y-2">
        <span className="text-xs font-mono-tactical uppercase text-[#e65100] tracking-widest">
          Testimonios de Alumnos
        </span>
        <p className="text-xs text-[#fffbf2]/60">
          Las valoraciones de los alumnos de las próximas promociones se publicarán al finalizar el ciclo.
        </p>
      </div>
    </div>
  </section>
);

const Asociacion = () => (
  <section id="asociacion" className="py-24 px-6 bg-[#fffbf2] text-[#0f2027]">
    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6 space-y-6">
        <span className="text-xs font-mono-tactical uppercase tracking-widest text-[#e65100]">
          Asociación Búsqueda y Salvamento
        </span>
        <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight text-[#0f2027]">
          Vocación, Servicio y Práctica
        </h2>
        <p className="text-sm md:text-base text-[#0f2027]/80 leading-relaxed">
          Nacemos con el compromiso de capacitar a voluntarios, profesionales y ciudadanos para intervenir con rigor
          ante cualquier emergencia en nuestra comunidad: desde la mar y la ría de Vigo hasta la montaña gallega.
        </p>

        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="p-4 bg-white border border-[#0f2027]/15 rounded">
            <span className="block font-display text-3xl text-[#e65100]">24/7</span>
            <span className="text-xs font-mono-tactical text-[#0f2027]/70 uppercase">Acceso Campus</span>
          </div>
          <div className="p-4 bg-white border border-[#0f2027]/15 rounded">
            <span className="block font-display text-3xl text-[#0f2027]">100%</span>
            <span className="text-xs font-mono-tactical text-[#0f2027]/70 uppercase">Galicia Operativa</span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-6">
        <figure className="relative aspect-[4/3] bg-[#f2e7d3] border-2 border-[#0f2027] rounded-lg shadow-[8px_8px_0px_#0f2027] overflow-hidden">
          <img
            src={INSTRUCTOR_IMG}
            alt="Prácticas reales de salvamento en Galicia"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <figcaption className="absolute bottom-5 left-5 text-xs uppercase tracking-[.14em] text-[#0f2027] font-mono-tactical bg-white/90 px-3 py-1.5 rounded border border-[#0f2027]/20">
            Prácticas reales de salvamento en Galicia
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
);

const Contacto = () => {
  const [form, setForm] = useState({ nombre: "", email: "", curso: "Primeros Auxilios Básicos - Montaña", mensaje: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Solicitud enviada. Te responderemos lo antes posible.");
      setForm({ nombre: "", email: "", curso: "Primeros Auxilios Básicos - Montaña", mensaje: "" });
    } catch (err) {
      toast.error("No se pudo enviar la solicitud. Escríbenos a info@busquedaysalvamento.es");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="py-24 px-6 bg-[#f7eedc] text-[#0f2027] border-t border-[#0f2027]/10">
      <div className="max-w-3xl mx-auto space-y-8 text-center">
        <div>
          <span className="text-xs font-mono-tactical uppercase tracking-widest text-[#e65100]">
            Inscripción & Contacto
          </span>
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight text-[#0f2027] mt-1">
            Únete a la Formación
          </h2>
          <p className="text-sm text-[#0f2027]/80 mt-2">
            Escríbenos para solicitar plaza o consultar dudas sobre nuestros cursos de teleformación.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="space-y-4 text-left bg-white p-8 rounded-lg border-2 border-[#0f2027] shadow-[6px_6px_0px_#0f2027]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="nombre" className="block text-xs font-mono-tactical uppercase text-[#0f2027] mb-1">
                Nombre completo
              </label>
              <input
                id="nombre"
                name="nombre"
                type="text"
                required
                value={form.nombre}
                onChange={handleChange}
                data-testid="contact-name-input"
                className="w-full px-4 py-3 text-sm bg-[#fffbf2] border border-[#0f2027]/30 rounded focus:border-[#e65100] outline-none"
                placeholder="Tu nombre"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-xs font-mono-tactical uppercase text-[#0f2027] mb-1">
                Correo electrónico
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                data-testid="contact-email-input"
                className="w-full px-4 py-3 text-sm bg-[#fffbf2] border border-[#0f2027]/30 rounded focus:border-[#e65100] outline-none"
                placeholder="tu@email.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="curso" className="block text-xs font-mono-tactical uppercase text-[#0f2027] mb-1">
              Curso de interés
            </label>
            <select
              id="curso"
              name="curso"
              value={form.curso}
              onChange={handleChange}
              data-testid="contact-course-select"
              className="w-full px-4 py-3 text-sm bg-[#fffbf2] border border-[#0f2027]/30 rounded focus:border-[#e65100] outline-none"
            >
              <option>Primeros Auxilios Básicos - Montaña</option>
              <option>Formación en Coeducación</option>
              <option>Salvamento Marítimo</option>
              <option>Búsqueda de Personas</option>
              <option>Información general / Voluntariado</option>
            </select>
          </div>

          <div>
            <label htmlFor="mensaje" className="block text-xs font-mono-tactical uppercase text-[#0f2027] mb-1">
              Mensaje o consulta
            </label>
            <textarea
              id="mensaje"
              name="mensaje"
              rows="3"
              value={form.mensaje}
              onChange={handleChange}
              data-testid="contact-message-input"
              className="w-full px-4 py-3 text-sm bg-[#fffbf2] border border-[#0f2027]/30 rounded focus:border-[#e65100] outline-none"
              placeholder="¿Tienes alguna duda sobre horarios o temario?"
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-btn"
            className="w-full py-4 bg-[#e65100] text-white text-xs font-mono-tactical uppercase tracking-widest font-bold rounded hover:bg-[#c2410c] transition-colors shadow disabled:opacity-60"
          >
            {sending ? "Enviando..." : "Enviar Solicitud"}
          </button>
        </form>

        <div className="text-xs font-mono-tactical text-[#0f2027]/70 flex items-center justify-center gap-2">
          <Mail className="w-3.5 h-3.5" aria-hidden="true" />
          Email directo:
          <a
            href="mailto:info@busquedaysalvamento.es"
            data-testid="contact-email-link"
            className="font-bold text-[#0f2027] hover:text-[#e65100] transition-colors"
          >
            info@busquedaysalvamento.es
          </a>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-[#0f2027] text-[#fffbf2] py-12 px-6 border-t border-white/10 text-xs font-mono-tactical">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <BrandMark className="w-6 h-6" />
        <span className="text-white uppercase tracking-wider">Asociación Búsqueda y Salvamento - Galicia</span>
      </div>
      <div className="text-[#fffbf2]/60 flex items-center gap-2">
        <LifeBuoy className="w-4 h-4 text-[#e65100]" aria-hidden="true" />
        Plataforma de Teleformación Oficial · Galicia
      </div>
      <a href="mailto:info@busquedaysalvamento.es" className="text-[#e65100] hover:underline flex items-center gap-2">
        <Radio className="w-3.5 h-3.5" aria-hidden="true" />
        info@busquedaysalvamento.es
      </a>
    </div>
  </footer>
);

export default function LandingPage() {
  return (
    <div data-testid="landing-page">
      <Toaster position="top-center" richColors />
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <Bienvenida />
        <Cursos />
        <Instructores />
        <Asociacion />
        <Contacto />
      </main>
      <Footer />
    </div>
  );
}
